import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { ReportData, ReportFiltersState } from '../hooks/useReports';
import type { SystemSettings } from '../hooks/useSystemSettings';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface PdfExportOptions {
  data: ReportData;
  filters: ReportFiltersState;
  settings: SystemSettings;
  activeTab: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtDate = (iso: string) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

const fmtPct = (v: number) => `${Math.round(v)}%`;

function periodLabel(filters: ReportFiltersState): string {
  if (filters.period === 'custom' && filters.customStart && filters.customEnd) {
    return `${fmtDate(filters.customStart)} a ${fmtDate(filters.customEnd)}`;
  }
  const map: Record<string, string> = {
    '1m': 'Último mês', '2m': 'Últimos 2 meses', '3m': 'Últimos 3 meses',
    '6m': 'Últimos 6 meses', '12m': 'Últimos 12 meses',
  };
  return map[filters.period] ?? 'Período selecionado';
}

function tabLabel(tab: string): string {
  const map: Record<string, string> = {
    'visao-geral': 'Visão Geral', fluxo: 'Fluxo de Caixa',
    categorias: 'Categorias', 'contas-cartoes': 'Contas & Cartões',
    metas: 'Metas', tabela: 'Tabela Analítica',
  };
  return map[tab] ?? 'Relatório';
}

function statusLabel(s: string): string {
  const map: Record<string, string> = {
    pago: 'Pago', recebido: 'Recebido', pendente: 'Pendente',
    vencido: 'Vencido', atrasado: 'Atrasado',
  };
  return map[s] ?? s;
}

function hexToRgb(hex: string): [number, number, number] {
  const cleaned = hex.replace('#', '');
  const r = parseInt(cleaned.substring(0, 2), 16);
  const g = parseInt(cleaned.substring(2, 4), 16);
  const b = parseInt(cleaned.substring(4, 6), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) return [16, 185, 129];
  return [r, g, b];
}

async function loadImageBase64(url: string): Promise<string | null> {
  try {
    const res = await fetch(url, { mode: 'cors' });
    const blob = await res.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// ─── Core Generator ──────────────────────────────────────────────────────────

export async function generatePdfReport(opts: PdfExportOptions): Promise<void> {
  const { data, filters, settings, activeTab } = opts;
  const primaryRgb = hexToRgb(settings.pdf_primary_color || settings.app_primary_color || '#10b981');
  const companyName = settings.pdf_company_name || settings.platform_name || 'PandaPro';
  const subtitle = settings.pdf_subtitle || 'Relatório Financeiro';
  const footerText = settings.pdf_footer_text || 'Documento gerado automaticamente pelo sistema';
  const legalNote = settings.pdf_legal_note || '';
  const showLogo = settings.pdf_show_logo !== 'false';
  const showSummary = settings.pdf_show_summary !== 'false';
  const showCategories = settings.pdf_show_categories !== 'false';
  const showTransactions = settings.pdf_show_transactions !== 'false';
  const filenamePrefix = settings.pdf_filename_prefix || 'relatorio';

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const marginL = 14;
  const marginR = 14;
  const contentW = pageW - marginL - marginR;
  const now = new Date();
  const generatedAt = now.toLocaleString('pt-BR');

  // ── Load logo ──────────────────────────────────────────────────────────────
  let logoBase64: string | null = null;
  const logoUrl = settings.pdf_logo_url || settings.logo_url || '';
  if (showLogo && logoUrl) {
    logoBase64 = await loadImageBase64(logoUrl);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // PAGE HEADER (drawn on every page via footer hook)
  // ─────────────────────────────────────────────────────────────────────────

  const addPageHeader = (pageNumber: number, totalPages: number) => {
    const p = doc;

    // Top accent bar
    p.setFillColor(...primaryRgb);
    p.rect(0, 0, pageW, 1.5, 'F');

    // Logo
    let logoEndX = marginL;
    if (logoBase64 && showLogo) {
      try {
        const ext = logoBase64.includes('data:image/png') ? 'PNG' : 'JPEG';
        p.addImage(logoBase64, ext, marginL, 4, 28, 10, undefined, 'FAST');
        logoEndX = marginL + 30;
      } catch { /* ignore logo errors */ }
    }

    // Company name and report title (right side)
    p.setFont('helvetica', 'bold');
    p.setFontSize(8);
    p.setTextColor(80, 80, 80);
    p.text(companyName, pageW - marginR, 8, { align: 'right' });
    p.setFont('helvetica', 'normal');
    p.setFontSize(7);
    p.setTextColor(140, 140, 140);
    p.text(subtitle, pageW - marginR, 12.5, { align: 'right' });

    // Separator line
    p.setDrawColor(...primaryRgb);
    p.setLineWidth(0.4);
    p.line(marginL, 16, pageW - marginR, 16);

    // Footer
    p.setDrawColor(220, 220, 220);
    p.setLineWidth(0.3);
    p.line(marginL, pageH - 10, pageW - marginR, pageH - 10);
    p.setFont('helvetica', 'normal');
    p.setFontSize(6.5);
    p.setTextColor(160, 160, 160);
    p.text(footerText, marginL, pageH - 6);
    if (legalNote) {
      p.text(legalNote, marginL, pageH - 3);
    }
    p.text(`Página ${pageNumber} de ${totalPages}`, pageW - marginR, pageH - 6, { align: 'right' });
    p.text(`Gerado em ${generatedAt}`, pageW - marginR, pageH - 3, { align: 'right' });
  };

  // ─────────────────────────────────────────────────────────────────────────
  // COVER / TITLE SECTION
  // ─────────────────────────────────────────────────────────────────────────

  let y = 22;

  // Big title block
  doc.setFillColor(...primaryRgb);
  doc.roundedRect(marginL, y, contentW, 26, 3, 3, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(255, 255, 255);
  doc.text(tabLabel(activeTab), marginL + 6, y + 9);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255, 0.85 as unknown as number);
  doc.text(`Período: ${periodLabel(filters)}`, marginL + 6, y + 16);
  doc.text(`Gerado em: ${generatedAt}`, marginL + 6, y + 21);

  // Filters applied
  const appliedFilters: string[] = [];
  if (filters.tipo !== 'todos') appliedFilters.push(`Tipo: ${filters.tipo === 'receita' ? 'Receitas' : 'Despesas'}`);
  if (filters.status !== 'todos') appliedFilters.push(`Status: ${statusLabel(filters.status)}`);
  if (filters.categoria) appliedFilters.push(`Categoria: ${filters.categoria}`);
  if (filters.origin !== 'todos') appliedFilters.push(`Origem: ${filters.origin === 'conta' ? 'Conta' : 'Cartão'}`);
  if (appliedFilters.length > 0) {
    doc.setFontSize(7);
    doc.text(`Filtros: ${appliedFilters.join(' · ')}`, pageW - marginR - 2, y + 21, { align: 'right' });
  }

  y += 32;

  // ─────────────────────────────────────────────────────────────────────────
  // EXECUTIVE SUMMARY CARDS
  // ─────────────────────────────────────────────────────────────────────────

  if (showSummary) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(40, 40, 40);
    doc.text('Resumo Executivo', marginL, y);
    y += 5;

    const cards = [
      { label: 'Total de Receitas', value: fmt(data.totalReceitas), color: [16, 185, 129] as [number, number, number] },
      { label: 'Total de Despesas', value: fmt(data.totalDespesas), color: [239, 68, 68] as [number, number, number] },
      { label: 'Saldo do Período', value: fmt(data.saldo), color: data.saldo >= 0 ? [16, 185, 129] as [number, number, number] : [239, 68, 68] as [number, number, number] },
      { label: 'Total Pendente', value: fmt(data.totalPendente), color: [245, 158, 11] as [number, number, number] },
    ];

    const cardW = (contentW - 6) / 4;
    cards.forEach((card, i) => {
      const cx = marginL + i * (cardW + 2);
      doc.setFillColor(248, 249, 250);
      doc.roundedRect(cx, y, cardW, 16, 2, 2, 'F');
      doc.setDrawColor(...card.color);
      doc.setLineWidth(0.5);
      doc.roundedRect(cx, y, cardW, 16, 2, 2, 'S');
      // Left accent
      doc.setFillColor(...card.color);
      doc.rect(cx, y, 1, 16, 'F');
      // Label
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.5);
      doc.setTextColor(120, 120, 120);
      doc.text(card.label, cx + 3.5, y + 5.5);
      // Value
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(...card.color);
      doc.text(card.value, cx + 3.5, y + 12);
    });

    y += 22;

    // Secondary metrics row
    const metrics = [
      { label: 'Nº de Lançamentos', value: String(data.transactionCount) },
      { label: 'Ticket Médio', value: fmt(data.ticketMedio) },
      { label: 'Média Diária', value: fmt(data.mediaDiaria) },
      { label: 'Rec. Recorrentes', value: fmtPct(data.pctRecorrentes) },
      { label: 'Parcelados', value: fmtPct(data.pctParcelados) },
      { label: 'Via Conta', value: fmt(data.distribConta) },
    ];
    const mW = (contentW - 5 * 2) / 6;
    metrics.forEach((m, i) => {
      const mx = marginL + i * (mW + 2);
      doc.setFillColor(250, 250, 250);
      doc.roundedRect(mx, y, mW, 12, 1.5, 1.5, 'F');
      doc.setDrawColor(230, 230, 230);
      doc.setLineWidth(0.3);
      doc.roundedRect(mx, y, mW, 12, 1.5, 1.5, 'S');
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(5.5);
      doc.setTextColor(150, 150, 150);
      doc.text(m.label, mx + mW / 2, y + 4, { align: 'center' });
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.setTextColor(50, 50, 50);
      doc.text(m.value, mx + mW / 2, y + 9.5, { align: 'center' });
    });

    y += 18;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // MONTHLY COMPARISON TABLE
  // ─────────────────────────────────────────────────────────────────────────

  if (data.monthlyData.length > 0) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(40, 40, 40);
    doc.text('Comparativo Mensal', marginL, y);
    y += 3;

    autoTable(doc, {
      startY: y,
      head: [['Mês', 'Receitas', 'Despesas', 'Saldo']],
      body: data.monthlyData.map(m => [
        m.mesLabel,
        fmt(m.receitas),
        fmt(m.despesas),
        fmt(m.saldo),
      ]),
      styles: {
        fontSize: 8,
        cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 3 },
        lineColor: [230, 230, 230],
        lineWidth: 0.2,
        textColor: [60, 60, 60],
      },
      headStyles: {
        fillColor: primaryRgb,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 8,
      },
      alternateRowStyles: { fillColor: [248, 249, 250] },
      columnStyles: {
        0: { cellWidth: 35 },
        1: { halign: 'right', textColor: [16, 185, 129] },
        2: { halign: 'right', textColor: [239, 68, 68] },
        3: { halign: 'right', fontStyle: 'bold' },
      },
      margin: { left: marginL, right: marginR },
    });

    y = (doc as any).lastAutoTable.finalY + 8;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // CATEGORY BREAKDOWN (Expenses)
  // ─────────────────────────────────────────────────────────────────────────

  if (showCategories && data.expensesByCategory.length > 0) {
    if (y > pageH - 60) { doc.addPage(); y = 22; }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(40, 40, 40);
    doc.text('Gastos por Categoria', marginL, y);
    y += 3;

    autoTable(doc, {
      startY: y,
      head: [['Categoria', 'Total', '%', 'Lançamentos']],
      body: data.expensesByCategory.map(c => [
        c.category,
        fmt(c.total),
        fmtPct(c.percent),
        String(c.count),
      ]),
      styles: {
        fontSize: 8,
        cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 3 },
        lineColor: [230, 230, 230],
        lineWidth: 0.2,
        textColor: [60, 60, 60],
      },
      headStyles: {
        fillColor: primaryRgb,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 8,
      },
      alternateRowStyles: { fillColor: [248, 249, 250] },
      columnStyles: {
        0: { cellWidth: 70 },
        1: { halign: 'right', textColor: [239, 68, 68] },
        2: { halign: 'right' },
        3: { halign: 'right' },
      },
      margin: { left: marginL, right: marginR },
    });

    y = (doc as any).lastAutoTable.finalY + 8;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // CATEGORY BREAKDOWN (Income)
  // ─────────────────────────────────────────────────────────────────────────

  if (showCategories && data.incomeByCategory.length > 0) {
    if (y > pageH - 60) { doc.addPage(); y = 22; }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(40, 40, 40);
    doc.text('Receitas por Categoria', marginL, y);
    y += 3;

    autoTable(doc, {
      startY: y,
      head: [['Categoria', 'Total', '%', 'Lançamentos']],
      body: data.incomeByCategory.map(c => [
        c.category,
        fmt(c.total),
        fmtPct(c.percent),
        String(c.count),
      ]),
      styles: {
        fontSize: 8,
        cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 3 },
        lineColor: [230, 230, 230],
        lineWidth: 0.2,
        textColor: [60, 60, 60],
      },
      headStyles: {
        fillColor: [16, 185, 129],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 8,
      },
      alternateRowStyles: { fillColor: [248, 249, 250] },
      columnStyles: {
        0: { cellWidth: 70 },
        1: { halign: 'right', textColor: [16, 185, 129] },
        2: { halign: 'right' },
        3: { halign: 'right' },
      },
      margin: { left: marginL, right: marginR },
    });

    y = (doc as any).lastAutoTable.finalY + 8;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // ACCOUNT BREAKDOWN (if contas-cartoes tab)
  // ─────────────────────────────────────────────────────────────────────────

  if (activeTab === 'contas-cartoes' && data.accountBreakdown.length > 0) {
    if (y > pageH - 60) { doc.addPage(); y = 22; }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(40, 40, 40);
    doc.text('Contas Bancárias', marginL, y);
    y += 3;

    autoTable(doc, {
      startY: y,
      head: [['Conta', 'Saldo Atual', 'Receitas', 'Despesas', 'Lançamentos', '% Despesas']],
      body: data.accountBreakdown.map(a => [
        a.name,
        fmt(a.currentBalance),
        fmt(a.totalIncome),
        fmt(a.totalExpenses),
        String(a.transactionCount),
        fmtPct(a.pct),
      ]),
      styles: {
        fontSize: 7.5,
        cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 3 },
        lineColor: [230, 230, 230],
        lineWidth: 0.2,
      },
      headStyles: {
        fillColor: primaryRgb,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 7.5,
      },
      alternateRowStyles: { fillColor: [248, 249, 250] },
      columnStyles: {
        1: { halign: 'right' },
        2: { halign: 'right', textColor: [16, 185, 129] },
        3: { halign: 'right', textColor: [239, 68, 68] },
        4: { halign: 'right' },
        5: { halign: 'right' },
      },
      margin: { left: marginL, right: marginR },
    });

    y = (doc as any).lastAutoTable.finalY + 8;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // CARD BREAKDOWN
  // ─────────────────────────────────────────────────────────────────────────

  if (activeTab === 'contas-cartoes' && data.cardBreakdown.length > 0) {
    if (y > pageH - 60) { doc.addPage(); y = 22; }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(40, 40, 40);
    doc.text('Cartões de Crédito', marginL, y);
    y += 3;

    autoTable(doc, {
      startY: y,
      head: [['Cartão', 'Total Gasto', 'Pendente', 'Lançamentos', '% Total']],
      body: data.cardBreakdown.map(c => [
        c.name,
        fmt(c.totalExpenses),
        fmt(c.pendingAmount),
        String(c.transactionCount),
        fmtPct(c.pct),
      ]),
      styles: {
        fontSize: 7.5,
        cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 3 },
        lineColor: [230, 230, 230],
        lineWidth: 0.2,
      },
      headStyles: {
        fillColor: primaryRgb,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 7.5,
      },
      alternateRowStyles: { fillColor: [248, 249, 250] },
      columnStyles: {
        1: { halign: 'right', textColor: [239, 68, 68] },
        2: { halign: 'right', textColor: [245, 158, 11] },
        3: { halign: 'right' },
        4: { halign: 'right' },
      },
      margin: { left: marginL, right: marginR },
    });

    y = (doc as any).lastAutoTable.finalY + 8;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // GOALS (if metas tab)
  // ─────────────────────────────────────────────────────────────────────────

  if (activeTab === 'metas' && data.goalsVsReal.length > 0) {
    if (y > pageH - 60) { doc.addPage(); y = 22; }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(40, 40, 40);
    doc.text('Metas vs. Realizado', marginL, y);
    y += 3;

    autoTable(doc, {
      startY: y,
      head: [['Meta', 'Alvo', 'Realizado', 'Progresso', 'Necessário/mês', 'Status']],
      body: data.goalsVsReal.map(g => [
        g.name,
        fmt(g.targetAmount),
        fmt(g.currentAmount),
        fmtPct(g.progress),
        g.monthlyNeeded != null ? fmt(g.monthlyNeeded) : '—',
        g.isAtRisk ? '⚠ Em risco' : '✓ No ritmo',
      ]),
      styles: {
        fontSize: 7.5,
        cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 3 },
        lineColor: [230, 230, 230],
        lineWidth: 0.2,
      },
      headStyles: {
        fillColor: primaryRgb,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 7.5,
      },
      alternateRowStyles: { fillColor: [248, 249, 250] },
      columnStyles: {
        1: { halign: 'right' },
        2: { halign: 'right', textColor: [16, 185, 129] },
        3: { halign: 'right' },
        4: { halign: 'right' },
        5: { halign: 'center' },
      },
      margin: { left: marginL, right: marginR },
    });

    y = (doc as any).lastAutoTable.finalY + 8;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // CASH FLOW (if fluxo tab)
  // ─────────────────────────────────────────────────────────────────────────

  if (activeTab === 'fluxo') {
    if (y > pageH - 60) { doc.addPage(); y = 22; }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(40, 40, 40);
    doc.text('Projeção de Saldo', marginL, y);
    y += 3;

    const bp = data.balanceProjection;
    const riskMap: Record<string, string> = { safe: 'Saudável', attention: 'Atenção', danger: 'Risco' };
    autoTable(doc, {
      startY: y,
      head: [['Indicador', 'Valor']],
      body: [
        ['Saldo Atual', fmt(bp.currentBalance)],
        ['Receitas Esperadas (mês)', fmt(bp.expectedIncome)],
        ['Despesas Pendentes (mês)', fmt(bp.pendingExpenses)],
        ['Valor Vencido', fmt(bp.overdueAmount)],
        ['Saldo Estimado (fim do mês)', fmt(bp.estimatedEndBalance)],
        ['Nível de Risco', riskMap[bp.riskLevel] ?? bp.riskLevel],
      ],
      styles: {
        fontSize: 8,
        cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 3 },
        lineColor: [230, 230, 230],
        lineWidth: 0.2,
      },
      headStyles: {
        fillColor: primaryRgb,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 8,
      },
      columnStyles: {
        0: { cellWidth: 80, fontStyle: 'bold' },
        1: { halign: 'right' },
      },
      margin: { left: marginL, right: marginR },
      tableWidth: 120,
    });

    y = (doc as any).lastAutoTable.finalY + 8;

    // Cash flow items (next 30 days)
    if (data.cashFlow30.items.length > 0) {
      if (y > pageH - 60) { doc.addPage(); y = 22; }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(40, 40, 40);
      doc.text('Próximos 30 Dias', marginL, y);
      y += 3;

      autoTable(doc, {
        startY: y,
        head: [['Data', 'Descrição', 'Categoria', 'Tipo', 'Valor']],
        body: data.cashFlow30.items.slice(0, 30).map(item => [
          fmtDate(item.date),
          item.description,
          item.category,
          item.type === 'receita' ? 'Receita' : 'Despesa',
          fmt(item.amount),
        ]),
        styles: {
          fontSize: 7.5,
          cellPadding: { top: 2, bottom: 2, left: 3, right: 3 },
          lineColor: [230, 230, 230],
          lineWidth: 0.2,
          overflow: 'ellipsize',
        },
        headStyles: {
          fillColor: primaryRgb,
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 7.5,
        },
        alternateRowStyles: { fillColor: [248, 249, 250] },
        columnStyles: {
          0: { cellWidth: 20 },
          1: { cellWidth: 65 },
          2: { cellWidth: 35 },
          3: { halign: 'center', cellWidth: 22 },
          4: { halign: 'right', cellWidth: 30 },
        },
        margin: { left: marginL, right: marginR },
      });

      y = (doc as any).lastAutoTable.finalY + 8;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // TRANSACTIONS TABLE
  // ─────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────
  // PAYEE BREAKDOWN (if favorecidos tab or always include if data available)
  // ─────────────────────────────────────────────────────────────────────────

  if (data.payeeBreakdown && data.payeeBreakdown.length > 0) {
    if (y > pageH - 60) { doc.addPage(); y = 22; }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(40, 40, 40);
    doc.text('Gastos por Favorecido', marginL, y);
    y += 3;

    autoTable(doc, {
      startY: y,
      head: [['Favorecido', 'Total', '%', 'Lançamentos', 'Média Mensal']],
      body: data.payeeBreakdown.slice(0, 20).map(p => [
        p.payee_name,
        fmt(p.total),
        `${p.percent}%`,
        String(p.count),
        fmt(p.avgPerMonth),
      ]),
      styles: {
        fontSize: 8,
        cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 3 },
        lineColor: [230, 230, 230],
        lineWidth: 0.2,
        textColor: [60, 60, 60],
      },
      headStyles: {
        fillColor: primaryRgb,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 8,
      },
      alternateRowStyles: { fillColor: [248, 249, 250] },
      columnStyles: {
        0: { cellWidth: 65 },
        1: { halign: 'right', textColor: [239, 68, 68] },
        2: { halign: 'right' },
        3: { halign: 'right' },
        4: { halign: 'right', textColor: [245, 158, 11] },
      },
      margin: { left: marginL, right: marginR },
    });

    y = (doc as any).lastAutoTable.finalY + 8;
  }

  if (showTransactions && data.filteredTransactions.length > 0) {
    doc.addPage();
    y = 22;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(40, 40, 40);
    doc.text(`Lançamentos — ${data.filteredTransactions.length} registros`, marginL, y);
    y += 3;

    const maxTx = 500;
    const txBody = data.filteredTransactions.slice(0, maxTx).map(t => [
      fmtDate(t.date),
      t.description.length > 30 ? t.description.substring(0, 30) + '...' : t.description,
      (t as any).payee_name ? ((t as any).payee_name as string).substring(0, 18) : '—',
      t.category,
      t.type === 'receita' ? 'Receita' : 'Despesa',
      t.originName,
      statusLabel(t.status),
      fmt(t.amount),
    ]);

    autoTable(doc, {
      startY: y,
      head: [['Data', 'Descrição', 'Favorecido', 'Categoria', 'Tipo', 'Origem', 'Status', 'Valor']],
      body: txBody,
      styles: {
        fontSize: 7,
        cellPadding: { top: 1.8, bottom: 1.8, left: 2.5, right: 2.5 },
        lineColor: [230, 230, 230],
        lineWidth: 0.2,
        overflow: 'ellipsize',
        textColor: [60, 60, 60],
      },
      headStyles: {
        fillColor: primaryRgb,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 7,
      },
      alternateRowStyles: { fillColor: [248, 249, 250] },
      columnStyles: {
        0: { cellWidth: 17 },
        1: { cellWidth: 42 },
        2: { cellWidth: 25 },
        3: { cellWidth: 25 },
        4: { halign: 'center', cellWidth: 16 },
        5: { cellWidth: 20 },
        6: { halign: 'center', cellWidth: 16 },
        7: { halign: 'right', fontStyle: 'bold', cellWidth: 21 },
      },
      didParseCell: (hookData) => {
        if (hookData.column.index === 4 && hookData.section === 'body') {
          const val = hookData.cell.raw as string;
          hookData.cell.styles.textColor = val === 'Receita' ? [16, 185, 129] : [239, 68, 68];
        }
        if (hookData.column.index === 6 && hookData.section === 'body') {
          const val = hookData.cell.raw as string;
          if (val === 'Pago' || val === 'Recebido') hookData.cell.styles.textColor = [16, 185, 129];
          else if (val === 'Pendente') hookData.cell.styles.textColor = [245, 158, 11];
          else if (val === 'Vencido' || val === 'Atrasado') hookData.cell.styles.textColor = [239, 68, 68];
        }
        if (hookData.column.index === 7 && hookData.section === 'body') {
          hookData.cell.styles.textColor = [50, 50, 50];
        }
      },
      margin: { left: marginL, right: marginR },
      rowPageBreak: 'avoid',
      showHead: 'everyPage',
    });

    if (data.filteredTransactions.length > maxTx) {
      const finalY = (doc as any).lastAutoTable.finalY + 4;
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(7);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `* Exibindo ${maxTx} de ${data.filteredTransactions.length} registros. Exporte em CSV para ver todos.`,
        marginL, finalY
      );
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // POST-PROCESS: add headers/footers to ALL pages
  // ─────────────────────────────────────────────────────────────────────────

  const totalPages = doc.getNumberOfPages();
  for (let pg = 1; pg <= totalPages; pg++) {
    doc.setPage(pg);
    addPageHeader(pg, totalPages);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // SAVE
  // ─────────────────────────────────────────────────────────────────────────

  const dateStr = now.toLocaleDateString('pt-BR').replace(/\//g, '-');
  const tabSlug = activeTab.replace(/[^a-z0-9]/gi, '-');
  doc.save(`${filenamePrefix}-${tabSlug}-${dateStr}.pdf`);
}
